import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "./BuyPage.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const BuyPage = ({ filters }) => {
  const [sellPosts, setSellPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingBike, setPendingBike] = useState(null);
  const [unlockedContacts, setUnlockedContacts] = useState({}); // per-bike tracking
  const [contactsUnlockedCount, setContactsUnlockedCount] = useState(0); // total credits

  const userInfo = JSON.parse(localStorage.getItem("user"));
  const isLoggedIn = !!userInfo;
  const cardsPerPage = 9;

  // 🚀 FETCH POSTS
  useEffect(() => {
    const fetchSellPosts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/post/getallposts`);
        const data = await res.json();
        const filteredPosts = data
          .filter((post) => post.postType === "Sell")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setSellPosts(filteredPosts);
      } catch (err) {
        console.error("Error fetching sell posts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSellPosts();
    const handlePostDeleted = () => fetchSellPosts();
    window.addEventListener("postDeleted", handlePostDeleted);
    return () => window.removeEventListener("postDeleted", handlePostDeleted);
  }, []);

  // 🚀 FETCH USER CONTACT CREDITS ON LOAD
  useEffect(() => {
    if (isLoggedIn) {
      fetch(`${process.env.REACT_APP_API_URL}/api/payment/contacts`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setContactsUnlockedCount(data.contactsUnlocked || 0);
          }
        })
        .catch(err => console.error("Failed to fetch credits:", err));
    }
  }, [isLoggedIn, userInfo?.token]);

  // Filtering logic (unchanged)
  const filteredSellPosts = sellPosts.filter((post) => {
    const price = Number(
      typeof post.price === "number" ? post.price : post.price?.replace(/,/g, "")
    );
    return (
      (!filters.location ||
        post.state?.toLowerCase().includes(filters.location.toLowerCase()) ||
        post.area?.toLowerCase().includes(filters.location.toLowerCase()) ||
        post.city?.toLowerCase().includes(filters.location.toLowerCase()) ||
        String(post.pincode || "").includes(filters.location)) &&
      (!filters.brand || post.brand?.toLowerCase().includes(filters.brand.toLowerCase())) &&
      (!filters.ownership || post.ownership?.toLowerCase() === filters.ownership.toLowerCase()) &&
      (!filters.year || String(post.year) === String(filters.year)) &&
      (!filters.budget ||
        (price >= parseInt(filters.budget) - 20000 &&
          price <= parseInt(filters.budget) + 20000)) &&
      (!filters.bikeType || post.bikeType?.toLowerCase() === filters.bikeType.toLowerCase())
    );
  });

  const indexOfLastPost = currentPage * cardsPerPage;
  const indexOfFirstPost = indexOfLastPost - cardsPerPage;
  const currentPosts = filteredSellPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredSellPosts.length / cardsPerPage);

  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 700, behavior: "smooth" });
  };

  const maxPageNumbers = 3;
  const getPageNumbers = () => {
    let startPage = Math.max(currentPage - Math.floor(maxPageNumbers / 2), 1);
    let endPage = startPage + maxPageNumbers - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(endPage - maxPageNumbers + 1, 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  // Slider arrows (unchanged)
  const NextArrow = (props) => (
    <div className={props.className} style={{ ...props.style, display: "block", right: 10, zIndex: 1 }} onClick={props.onClick} />
  );
  const PrevArrow = (props) => (
    <div className={props.className} style={{ ...props.style, display: "block", left: 10, zIndex: 1 }} onClick={props.onClick} />
  );
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const getImageUrl = (img) => {
    if (img && img.startsWith("http")) return img;
    return `${process.env.REACT_APP_API_URL}/${img}`;
  };

  const toggleExpand = (id) => setExpandedPostId(expandedPostId === id ? null : id);

  // ✅ RAZORPAY PAYMENT (FIXED - SENDS PURPOSE)
  const openRazorpay = async () => {
    try {
      const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/order`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${userInfo.token}` 
        },
        body: JSON.stringify({ 
          purpose: "view_contacts" // 👈 THIS IS KEY!
        }),
      });
      const orderData = await orderRes.json();
      
      return new Promise((resolve) => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: orderData.amount,
          currency: "INR",
          name: "BikeBechoo",
          description: "Unlock 3 Contact Views (₹59)",
          order_id: orderData.id,
          handler: async (response) => {
            const verifyRes = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/verify`, {
              method: "POST",
              headers: { 
                "Content-Type": "application/json", 
                Authorization: `Bearer ${userInfo.token}` 
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                purpose: "view_contacts", // 👈 THIS IS KEY!
              }),
            });
            const verifyData = await verifyRes.json();
            resolve(verifyData.success === true);
          },
          modal: { ondismiss: () => resolve(false) },
          theme: { color: "#3399cc" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      });
    } catch (err) {
      console.error("Razorpay error:", err);
      return false;
    }
  };

  // ✅ HANDLE CONTACT CLICK (COMPLETELY FIXED)
  const handleContactClick = async (e, post) => {
    e.stopPropagation(); // Prevent card expand
    
    if (unlockedContacts[post._id]) {
      return; // Already unlocked
    }

    if (contactsUnlockedCount > 0) {
      // ✅ Use 1 credit immediately
      setUnlockedContacts(prev => ({ ...prev, [post._id]: true }));
      setContactsUnlockedCount(prev => prev - 1);

      // Sync with backend
      fetch(`${process.env.REACT_APP_API_URL}/api/payment/use-contact`, {
        method: "POST",
        headers: { Authorization: `Bearer ${userInfo.token}` },
      }).catch(err => console.error("Failed to decrement:", err));
    } else {
      // No credits - show payment
      setPendingBike(post);
      setShowPaymentModal(true);
    }
  };

  // ✅ HANDLE PAYMENT SUCCESS (FIXED)
  const handlePaymentContinue = async () => {
    setShowPaymentModal(false);
    if (!pendingBike) return;

    const paid = await openRazorpay();
    if (!paid) {
      alert("Payment failed or cancelled");
      return;
    }

    // ✅ Refresh credits from backend
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/contacts`, {
      headers: { Authorization: `Bearer ${userInfo.token}` },
    });
    const data = await res.json();
    
    if (data.success) {
      setContactsUnlockedCount(data.contactsUnlocked);
      
      // ✅ Unlock the pending bike immediately
      setUnlockedContacts(prev => ({ ...prev, [pendingBike._id]: true }));
      
      alert(`✅ Success! You now have ${data.contactsUnlocked} contact views remaining.`);
    }

    setPendingBike(null);
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  return (
    <div className="buy-page">
      <h2 className="buy-heading">"Your Ride Starts Here"</h2>
      <div className="scroll-container">
        <span className="buy-subheading">
          Buy bikes easily, safely, and at the best value — all in one place.
          {isLoggedIn && contactsUnlockedCount > 0 && (
            <span> | Contact Views Left: <strong>{contactsUnlockedCount}</strong></span>
          )}
        </span>
      </div>

      {loading ? (
        <p>Loading bikes...</p>
      ) : currentPosts.length === 0 ? (
        <p>No bikes found matching your filters.</p>
      ) : (
        <>
          {currentPosts.map((post) => {
            const isExpanded = expandedPostId === post._id;
            const isUnlocked = !!unlockedContacts[post._id];
            return (
              <div key={post._id} className={`bike-card ${isExpanded ? "expanded" : ""}`}>
                <div onClick={() => toggleExpand(post._id)} style={{ cursor: "pointer" }}>
                  <div className="bike-image-section">
                    <Slider {...sliderSettings}>
                      {post.bikeImages?.map((img, idx) => (
                        <div key={idx}>
                          <img
                            src={getImageUrl(img)}
                            alt={`Bike ${idx + 1}`}
                            className="bike-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/300x200";
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                    <span className="badge">SELL</span>
                    <div className="post-id-badge">{post.postId}</div>
                  </div>
                  <div className="bike-info">
                    <h3 className="bike-title">{post.brand} {post.model}</h3>
                    <p className="bike-location">{post.area}, {post.state}</p>
                    <div className="bike-meta">
                      <p><strong>Year:</strong> {post.year}</p>
                      <p><strong>Price:</strong> ₹{parseInt(post.price).toLocaleString("en-IN")}</p>
                    </div>
                    <p className="bike-description"><strong>Ownership:</strong> {post.ownership}</p>
                    <p><strong>Posted on:</strong> {new Date(post.createdAt).toLocaleDateString("en-GB")}</p>
                  </div>
                </div>

                {isExpanded && (
                  <div className="expanded-section">
                    <div className="bike-details">
                      <p><strong>Fuel Type:</strong> {post.fueltype}</p>
                      <p><strong>Kilometers Driven:</strong> {post.kilometerdriven}</p>
                      <p><strong>Engine Capacity:</strong> {post.enginecpacity}</p>
                      <p><strong>Description:</strong> {post.anymodification}</p>
                      <p><strong>RC Number:</strong> {post.rcNumber}</p>
                      <p><strong>Insurance:</strong> {post.insurancestatus}</p>
                      <p><strong>Contact Name:</strong> {post.contactName}</p>
                    </div>

                    {/* ✅ FIXED CONTACT DISPLAY LOGIC */}
                    {isUnlocked ? (
                      <div className="contact-unlocked">
                        <strong>📞 Contact:</strong> {post.contactMobile}
                      </div>
                    ) : isLoggedIn ? (
                      <button 
                        className="contact-btn"
                        onClick={(e) => handleContactClick(e, post)}
                      >
                        {contactsUnlockedCount > 0 
                          ? `View Contact (${contactsUnlockedCount} left)` 
                          : "Pay ₹59 to Unlock Contacts"
                        }
                      </button>
                    ) : (
                      <a href="https://bike-bechoo-6.onrender.com/auth/google">
                        <button className="contact-btn login-button">Login to view contact</button>
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination (unchanged) */}
          <div className="pagination">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>
            {getPageNumbers().map((page) => (
              <button 
                key={page} 
                onClick={() => goToPage(page)} 
                className={page === currentPage ? "active" : ""}
              >
                {page}
              </button>
            ))}
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </>
      )}

      {/* ✅ PAYMENT MODAL (IMPROVED) */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Unlock 3 Contact Views</h3>
            <p>Pay <strong>₹59</strong> to view mobile numbers of <strong>3 bikes</strong>.</p>
            <p style={{ fontSize: "14px", color: "#666" }}>
              First post is free. Additional 3 posts: ₹59.
            </p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowPaymentModal(false)}>
                Cancel
              </button>
              <button className="pay-btn" onClick={handlePaymentContinue}>
                Pay ₹59 Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyPage;
